/**
 * Sub-hook for PO Lines export and import template functionality (CR-1558).
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { purchasingAPI } from '../../../../lib/api';
import { useView } from '../../../../contexts/ViewContext';
import { POLinesFilters } from './types';
import { buyerIdFilterParam } from './filters/BuyerIdMultiSelect';

interface UsePOLinesExportParams {
  filters: POLinesFilters;
  activeTab: string;
  /** CR-7232: the applied (debounced) toolbar quick-search term. */
  partSearch?: string;
  /**
   * CR-7400: the buyer codes the control is offering. The download has to
   * decide "is this selection all of them" on the same list the grid does, or
   * Select All would export fewer rows than the screen shows.
   */
  buyerIdOptions?: string[];
}

export function usePOLinesExport({
  filters, activeTab, partSearch, buyerIdOptions = [],
}: UsePOLinesExportParams) {
  const { getViewQueryParams } = useView();

  const [exporting, setExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  // CR-6679: Order Acknowledgements import modal state
  const [showAckImportModal, setShowAckImportModal] = useState(false);

  /**
   * Build query params that map the current filter/tab state to API filter keys.
   * Used by both export and import-template download.
   */
  const buildFilterParams = useCallback((forExport: boolean = false): Record<string, string> => {
    const queryParams: Record<string, string> = {};

    if (filters.poNumber) queryParams['po__po_number__icontains'] = filters.poNumber;
    if (filters.lineNumber) queryParams['line_number'] = filters.lineNumber;
    if (filters.supplier) queryParams['po__supplier'] = filters.supplier;
    else if (filters.supplierSearch) queryParams['po__supplier__name__icontains'] = filters.supplierSearch;
    if (filters.customer) queryParams['po__customer'] = filters.customer;
    if (filters.partNumber) queryParams['part_number__icontains'] = filters.partNumber;
    if (filters.partDescription) queryParams['part_description__icontains'] = filters.partDescription;
    if (filters.description) queryParams['description__icontains'] = filters.description;

    // CR-3413: Unified status filter - route to correct API field
    if (filters.status) {
      if (filters.status === 'on_hold') {
        queryParams['on_hold'] = 'true';
      } else if (filters.status === 'closed') {
        queryParams['status'] = 'received';
      } else if (filters.status === 'changed') {
        queryParams['acknowledge_status__in'] = 'change_proposed_supplier,change_proposed_customer';
      } else if (filters.status === 'wip') {
        queryParams['acknowledge_status__in'] = 'wip,accepted';
        if (!forExport) queryParams['on_hold'] = 'false';
      } else {
        queryParams['acknowledge_status'] = filters.status;
      }
    }

    if (filters.on_hold === 'yes') queryParams['on_hold'] = 'true';
    else if (filters.on_hold === 'no') queryParams['on_hold'] = 'false';
    // CR-7400: the export applies the same exact-match selection as the grid,
    // through the same helper, so Select All downloads every line rather than
    // only the ones that carry a code.
    const buyerIds = buyerIdFilterParam(filters.buyerId, buyerIdOptions);
    if (buyerIds) queryParams['buyer_id__in'] = buyerIds;
    if (filters.batchSerialNo) queryParams['batch_serial_no__icontains'] = filters.batchSerialNo;
    // CR-7418: the export is the grid in a file — same decision filter.
    if (filters.otifDecision) queryParams['otif_decision'] = filters.otifDecision;
    // CR-7416: the export honours the OTIF Status filter too — an export that
    // quietly widens the set the screen showed is the defect this pairing
    // exists to avoid.
    if (filters.otifVerdict) queryParams['otif_verdict'] = filters.otifVerdict;
    if (filters.supplierGroup) queryParams['po__supplier__group'] = filters.supplierGroup;
    // Same ODD range as the list (odd_date). The previous
    // original_delivery_date__* names match no filter on the server, so an
    // export silently ignored the ODD range and returned every line.
    if (filters.oddFrom) queryParams['odd_date__gte'] = filters.oddFrom;
    if (filters.oddTo) queryParams['odd_date__lte'] = filters.oddTo;
    if (filters.ppv === 'positive') queryParams['ppv__gt'] = '0';
    else if (filters.ppv === 'negative') queryParams['ppv__lt'] = '0';
    else if (filters.ppv === 'zero') queryParams['ppv'] = '0';
    if (filters.commodityGroup) queryParams['commodity_group'] = filters.commodityGroup;
    if (filters.commodityType) queryParams['commodity_type'] = filters.commodityType;
    // CR-7232: the toolbar quick-search term, so an export matches the grid the
    // user is looking at. Export only: the import-template branch builds its own
    // lightweight queryset that bypasses get_queryset(), so the backend would
    // ignore part_search there and hand back a template wider than the grid.
    if (forExport && partSearch) queryParams['part_search'] = partSearch;
    // CR-6348: reworked SO Lines filters — keep export/import-template in sync
    // with the list & count queries so an export honours the same filters.
    if (filters.companyCountry) queryParams['po__customer__country'] = filters.companyCountry;
    if (filters.division) queryParams['po__customer__group'] = filters.division;
    if (filters.supplierCountry) queryParams['po__supplier__country'] = filters.supplierCountry;
    // CR-7028: Supplier code filter via the Company's supplier_code field
    if (filters.supplierCode) queryParams['po__supplier__supplier_code__icontains'] = filters.supplierCode;
    if (filters.warehouse) queryParams['po__delivery_warehouse'] = filters.warehouse;
    if (filters.oddStatus) queryParams['otif_status'] = filters.oddStatus;
    if (filters.otdStatus) queryParams['otd_status_lspd'] = filters.otdStatus;
    if (filters.criticalOnly === 'true') queryParams['is_critical'] = 'true';

    // Tab-based filtering
    if (activeTab === 'open') queryParams['status__in'] = 'pending,partially_received';
    else if (activeTab === 'closed') queryParams['status'] = 'received';
    else if (activeTab === 'on_hold') queryParams['on_hold'] = 'true';
    else if (activeTab === 'needs_action') queryParams['needs_action'] = 'true';
    // CR-7418: the review pill's own population, or the file is a different
    // set from the grid it was downloaded from.
    else if (activeTab === 'otif_review') queryParams['review_candidates'] = 'true';

    // CR-7322: on the On Hold tab, narrow to who placed the hold when the
    // deep link (or filter) asks for it.
    if (activeTab === 'on_hold' && (filters.on_hold_placed_by === 'customer' || filters.on_hold_placed_by === 'supplier')) {
      queryParams['on_hold_placed_by'] = filters.on_hold_placed_by;
    }

    return queryParams;
  }, [filters, activeTab, partSearch]);

  // Export handler (CR-1558)
  const handleExport = useCallback(async (format: 'xlsx' | 'csv' = 'xlsx') => {
    try {
      setExporting(true);

      const queryParams = buildFilterParams(true);
      queryParams['file_format'] = format;

      const response = await purchasingAPI.exportLines(queryParams);
      const blob = new Blob([response.data], {
        type: format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `po_lines_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export completed successfully');
    } catch (err: unknown) {
      console.error('Export failed:', err);
      toast.error('Failed to export PO lines');
    } finally {
      setExporting(false);
    }
  }, [buildFilterParams]);

  // Build query params for the import template download (mirrors current view + filters)
  const getTemplateExportParams = useCallback((): Record<string, string> => {
    const viewParams = getViewQueryParams();
    const queryParams: Record<string, string> = {};

    // View context (site / group / company)
    if (viewParams['site'])    queryParams['site']    = String(viewParams['site']);
    if (viewParams['group'])   queryParams['group']   = String(viewParams['group']);
    if (viewParams['company']) queryParams['company'] = String(viewParams['company']);

    // Merge filter params
    const filterParams = buildFilterParams();
    Object.assign(queryParams, filterParams);

    return queryParams;
  }, [buildFilterParams, getViewQueryParams]);

  return {
    exporting,
    showImportModal,
    setShowImportModal,
    // CR-6679: Order Acknowledgements import modal
    showAckImportModal,
    setShowAckImportModal,
    handleExport,
    getTemplateExportParams,
  };
}
